import { ChevronRight, ChevronsLeft, ChevronsRight, ChevronsUpDown, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Avatar } from '../atoms/Avatar'
import { Icon } from '../atoms/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../atoms/Tooltip'

/**
 * Sidebar — canonical navigation rail.
 * Pencil: oMmqQ (Expanded) · dfALg (Collapsed) · Zw1D3/XdZrh (Light variants)
 *
 * Two modes:
 *   - expanded 260w (default) — glass variant with full labels + section headers
 *   - collapsed 64w — solid variant, icon-only, active pill, expand toggle
 *
 * Items may nest up to 3 levels deep via `children?: SidebarItem[]`. Parents
 * with children render as expand/collapse rows with an animated chevron and
 * indented child list. Animation uses CSS grid-template-rows fr-unit
 * interpolation — zero-JS, respects `prefers-reduced-motion`.
 *
 * Theme follows the global `.dark` class; every color is tokenised.
 */

export interface SidebarItem {
  key: string
  label: string
  /** Optional icon. Required at the top level of a section; optional in nested children. */
  icon?: LucideIcon
  iconClassName?: string
  badge?: React.ReactNode
  href?: string
  /** Recursive children. Up to 3 levels deep per the docs-site IA contract. */
  children?: SidebarItem[]
  /** Whether the row is expanded on first render. Ignored when children is empty. */
  defaultExpanded?: boolean
}

export interface SidebarSection {
  label?: string
  items: SidebarItem[]
}

export interface SidebarUser {
  name: string
  role: string
  initials: string
  avatarUrl?: string
}

export interface SidebarProps {
  collapsed?: boolean
  variant?: 'glass' | 'solid'
  logo?: React.ReactNode
  brand?: React.ReactNode
  /** Optional pill rendered next to the brand name (e.g. "Admin" / "Portal"). */
  badge?: React.ReactNode
  sections: SidebarSection[]
  activeKey: string
  user?: SidebarUser
  onNavigate?: (key: string) => void
  onToggleCollapse?: () => void
  onUserClick?: () => void
  className?: string
}

/** Section label — Bastion style: sans 11/600 uppercase, grey, generous top gap */
const SidebarSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--color-ia-sidebar-section-label)] mt-5 mb-1.5 px-3">
    {children}
  </div>
)

/**
 * Recursive: returns true if any descendant in the subtree matches activeKey.
 * Drives the "on-path" visual indicator on ancestor rows.
 */
function subtreeContainsKey(item: SidebarItem, activeKey: string): boolean {
  if (item.key === activeKey) return true
  if (!item.children) return false
  return item.children.some((child) => subtreeContainsKey(child, activeKey))
}

/**
 * Leaf row — the original 40h active with left 3px accent + green glow.
 * Indented per depth (12px per level beyond the section root).
 */
const SidebarLeafRow: React.FC<{
  item: SidebarItem
  active: boolean
  depth: number
  onClick?: () => void
}> = ({ item, active, depth, onClick }) => {
  // 12px per nesting level; first-level (depth=0) uses the original px-3 padding.
  const indentPx = depth * 12
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{ paddingLeft: 12 + indentPx }}
      className={cn(
        'relative flex items-center gap-3 w-full rounded-lg text-body-sm font-medium transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
        active
          ? [
              'h-10 pr-3',
              'bg-[var(--color-ia-sidebar-active-bg)]',
              'text-[var(--color-ia-sidebar-active-fg)] font-semibold',
            ].join(' ')
          : [
              'h-10 pr-3',
              'text-[var(--color-ia-sidebar-icon-default)]',
              'hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-sidebar-foreground)]',
            ].join(' '),
      )}
    >
      {item.icon ? (
        <Icon
          icon={item.icon}
          size="md"
          className={active ? 'text-[var(--color-ia-sidebar-active-fg)]' : 'text-[var(--color-ia-sidebar-icon-default)]'}
        />
      ) : (
        // Indent guide for leaves without their own icon (deep-nested children).
        <span aria-hidden className="w-4 self-stretch border-l border-[var(--color-ia-sidebar-border-glass)] ml-1" />
      )}
      <span className="flex-1 text-left truncate">{item.label}</span>
      {item.badge}
    </button>
  )
}

/**
 * Parent row — clickable to toggle expand/collapse. If `href` is set, click
 * also fires onNavigate (analogous to "go to section landing"); otherwise
 * the row only toggles open state. Chevron rotates 90° on expand.
 *
 * Animation uses the grid-template-rows trick: a wrapper grid switches
 * between `grid-template-rows: 0fr` (collapsed) and `1fr` (expanded). The
 * inner div has `min-height: 0; overflow: hidden;` so children clip cleanly.
 * Browsers interpolate fr units linearly — smooth height animation without
 * known content height. Respects `prefers-reduced-motion` (the transition
 * resolves to 0ms via the existing `@media (prefers-reduced-motion: reduce)`
 * rule on the wrapper class).
 */
const SidebarParentRow: React.FC<{
  item: SidebarItem
  activeKey: string
  depth: number
  onNavigate?: (key: string) => void
}> = ({ item, activeKey, depth, onNavigate }) => {
  const containsActive = subtreeContainsKey(item, activeKey)
  const [open, setOpen] = React.useState<boolean>(
    () => item.defaultExpanded ?? containsActive,
  )
  // Auto-expand if a descendant becomes active (e.g. router pushes a deep route).
  React.useEffect(() => {
    if (containsActive) setOpen(true)
  }, [containsActive])

  const childrenId = React.useId()
  const indentPx = depth * 12
  const isSelf = item.key === activeKey

  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={childrenId}
        onClick={() => {
          setOpen((v) => !v)
          if (item.href && onNavigate) onNavigate(item.key)
        }}
        style={{ paddingLeft: 12 + indentPx }}
        className={cn(
          'relative flex items-center gap-2 w-full rounded-lg text-body-sm font-medium transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
          isSelf
            ? [
                'h-10 pr-3',
                'bg-[var(--color-ia-sidebar-active-bg)]',
                'text-[var(--color-ia-sidebar-active-fg)] font-semibold',
              ].join(' ')
            : containsActive
              ? [
                  'h-10 pr-3',
                  // on-path: subtler tint, no left bar
                  'text-[var(--color-sidebar-foreground)]',
                  'hover:bg-[var(--color-ia-sidebar-active-glow)]',
                ].join(' ')
              : [
                  'h-10 pr-3',
                  'text-[var(--color-ia-sidebar-icon-default)]',
                  'hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-sidebar-foreground)]',
                ].join(' '),
        )}
      >
        {item.icon ? (
          <Icon
            icon={item.icon}
            size="md"
            className={
              isSelf
                ? 'text-[var(--color-ia-sidebar-active-fg)]'
                : 'text-[var(--color-ia-sidebar-icon-default)]'
            }
          />
        ) : (
          <span aria-hidden className="w-4 self-stretch border-l border-[var(--color-ia-sidebar-border-glass)] ml-1" />
        )}
        <span className="flex-1 text-left truncate">{item.label}</span>
        {item.badge}
        <Icon
          icon={ChevronRight}
          size="xs"
          aria-hidden
          className={cn(
            'transition-transform duration-200 motion-reduce:transition-none',
            open ? 'rotate-90' : 'rotate-0',
            isSelf
              ? 'text-[var(--color-ia-sidebar-active-fg)]'
              : 'text-ia-text-tertiary',
          )}
        />
      </button>

      {/* Animated wrapper. grid-template-rows 0fr -> 1fr interpolates linearly. */}
      <div
        id={childrenId}
        role="group"
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden min-h-0">
          <div className="flex flex-col gap-[2px] pt-[2px]">
            {item.children?.map((child) =>
              child.children && child.children.length > 0 ? (
                <SidebarParentRow
                  key={child.key}
                  item={child}
                  activeKey={activeKey}
                  depth={depth + 1}
                  onNavigate={onNavigate}
                />
              ) : (
                <SidebarLeafRow
                  key={child.key}
                  item={child}
                  active={child.key === activeKey}
                  depth={depth + 1}
                  onClick={() => onNavigate?.(child.key)}
                />
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Collapsed 40×40 icon tile: active = pill radius 20, default = radius 8 */
const SidebarColNavItem: React.FC<{
  item: SidebarItem
  active: boolean
  onClick?: () => void
}> = ({ item, active, onClick }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onClick}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center justify-center w-10 h-10 rounded-pill transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
          active
            ? [
                'bg-[var(--color-ia-sidebar-col-active-bg)]',
                'text-[var(--color-ia-sidebar-active-fg)]',
                'shadow-[0_0_0_1.5px_var(--color-ia-sidebar-active-border),0_0_0_5px_var(--color-ia-sidebar-active-glow)]',
              ].join(' ')
            : 'text-[var(--color-ia-sidebar-icon-default)] hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-sidebar-foreground)]',
        )}
      >
        {item.icon ? (
          <Icon icon={item.icon} size="md" className={active ? '' : 'text-[var(--color-ia-sidebar-icon-default)]'} />
        ) : null}
      </button>
    </TooltipTrigger>
    <TooltipContent side="right">{item.label}</TooltipContent>
  </Tooltip>
)

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      collapsed = false,
      variant,
      logo,
      brand = 'IntelliAuth',
      badge,
      sections,
      activeKey,
      user,
      onNavigate,
      onToggleCollapse,
      onUserClick,
      className,
    },
    ref,
  ) => {
    // expanded defaults to glass, collapsed defaults to solid
    const v = variant ?? (collapsed ? 'solid' : 'glass')
    const width = collapsed ? 64 : 260

    return (
      <TooltipProvider>
        <aside
          ref={ref}
          aria-label="Primary navigation"
          style={{ width }}
          className={cn(
            'flex flex-col shrink-0 h-full transition-[width] duration-300 ease-in-out',
            // Always the solid near-black Bastion rail (opaque). The previous
            // glass variant referenced undefined --ia-sidebar-* vars (missing
            // the --color- prefix), so the aside had no background and showed
            // the light page through it. Use the real --color- tokens.
            v === 'glass'
              ? 'bg-[var(--color-ia-sidebar-bg-solid)] border-r border-r-[var(--color-ia-sidebar-border-glass)]'
              : 'bg-[var(--color-ia-sidebar-bg-solid)] border-r border-r-[var(--color-ia-sidebar-border-glass)]',
            className,
          )}
        >
          {/* Logo area */}
          <div
            className={cn(
              'flex items-center shrink-0 border-b border-[var(--color-ia-sidebar-border-glass)]',
              collapsed ? 'h-16 justify-center' : 'h-16 justify-between px-4',
            )}
          >
            {collapsed ? (
              (logo ?? <DefaultLogo />)
            ) : (
              <div className="flex items-center gap-2.5 min-w-0">
                {logo ?? <DefaultLogo />}
                <span className="font-semibold text-body-md text-[var(--color-sidebar-foreground)] tracking-[-0.3px] truncate">
                  {brand}
                </span>
                {badge}
              </div>
            )}
          </div>

          {/* Nav scroll */}
          <nav
            className={cn(
              'flex-1 overflow-y-auto overflow-x-hidden',
              collapsed ? 'py-4' : 'px-2 py-3',
            )}
          >
            {collapsed ? (
              <div className="flex flex-col items-center gap-1">
                {sections.map((section, si) => (
                  <React.Fragment key={si}>
                    {si > 0 && <div className="h-px w-6 bg-ia-border my-1" />}
                    {section.items.map((item) => (
                      <SidebarColNavItem
                        key={item.key}
                        item={item}
                        active={item.key === activeKey}
                        onClick={() => onNavigate?.(item.key)}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-[2px]">
                {sections.map((section, si) => (
                  <React.Fragment key={si}>
                    {section.label && <SidebarSectionLabel>{section.label}</SidebarSectionLabel>}
                    {section.items.map((item) =>
                      item.children && item.children.length > 0 ? (
                        <SidebarParentRow
                          key={item.key}
                          item={item}
                          activeKey={activeKey}
                          depth={0}
                          onNavigate={onNavigate}
                        />
                      ) : (
                        <SidebarLeafRow
                          key={item.key}
                          item={item}
                          active={item.key === activeKey}
                          depth={0}
                          onClick={() => onNavigate?.(item.key)}
                        />
                      ),
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="shrink-0 border-t border-[var(--color-ia-sidebar-border-glass)]">
            {/* User block (expanded only) */}
            {!collapsed && user && (
              <button
                type="button"
                onClick={onUserClick}
                className="flex items-center gap-3 px-4 h-14 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
              >
                <Avatar size="sm" initials={user.initials} src={user.avatarUrl} />
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="text-caption font-medium text-[var(--color-sidebar-foreground)] truncate">
                    {user.name}
                  </span>
                  <span className="font-mono text-[10px] text-ia-text-tertiary truncate">
                    {user.role}
                  </span>
                </div>
                <Icon icon={ChevronsUpDown} size="xs" className="text-ia-text-tertiary" />
              </button>
            )}

            {/* User avatar (collapsed only) */}
            {collapsed && user && (
              <div className="flex justify-center py-3">
                <button
                  type="button"
                  onClick={onUserClick}
                  aria-label={user.name}
                  className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-pill"
                >
                  <Avatar size="md" initials={user.initials} src={user.avatarUrl} />
                </button>
              </div>
            )}

            {/* Collapse/Expand toggle */}
            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={cn(
                  'flex items-center w-full border-t border-[var(--color-ia-sidebar-border-glass)] transition-colors',
                  'text-ia-text-tertiary hover:bg-ia-surface-hover hover:text-[var(--color-sidebar-foreground)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  collapsed ? 'justify-center h-10' : 'gap-2 px-4 h-10',
                )}
              >
                <Icon icon={collapsed ? ChevronsRight : ChevronsLeft} size="xs" />
                {!collapsed && <span className="text-xs">Collapse</span>}
              </button>
            )}
          </div>
        </aside>
      </TooltipProvider>
    )
  },
)
Sidebar.displayName = 'Sidebar'

/** Default gradient logo mark (matches Pencil gLAOw logoIcon) */
const DefaultLogo: React.FC = () => (
  <div
    className="h-7 w-7 rounded-lg shrink-0"
    style={{
      background: 'linear-gradient(180deg, #4DD9A0 0%, #1B2A4E 100%)',
    }}
    aria-hidden
  />
)
